import { BASE_URL } from "../../api/url";
import type {
  DietType,
  IngredientActivityAction,
  IngredientDetail,
} from "./types";

const dietTypeFromApi = (type: unknown): DietType => {
  const t = typeof type === "string" ? type.toUpperCase() : "";
  if (t === "NON_VEG" || t === "NONVEG") return "nonveg";
  if (t === "VEG") return "veg";
  return "none";
};

// Media paths come back as "/media/..." — prefix BASE_URL so the browser can
// resolve them. Absolute URLs (http/https) are passed through unchanged.
const resolvePictureUrl = (picture: unknown): string | null => {
  if (!picture || typeof picture !== "string") return null;
  if (/^https?:\/\//i.test(picture)) return picture;
  return `${BASE_URL}${picture.startsWith("/") ? "" : "/"}${picture}`;
};

const toNumber = (value: unknown): number => {
  const parsed =
    typeof value === "number" ? value : parseFloat(String(value ?? ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatActivityStamp = (
  isoStamp: unknown,
): { date: string; time: string } => {
  const raw = typeof isoStamp === "string" ? isoStamp : "";
  const parsed = raw ? new Date(raw) : null;
  if (!parsed || Number.isNaN(parsed.getTime())) {
    return { date: raw, time: "" };
  }
  const date = parsed.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  const time = parsed.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  return { date, time };
};

const parseMicronutrients = (raw: unknown): Record<string, string> => {
  if (!raw || typeof raw !== "object") return {};
  const out: Record<string, string> = {};
  Object.entries(raw as Record<string, unknown>).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      out[key] = "";
    } else {
      out[key] = String(value);
    }
  });
  return out;
};

// Convert the food_data payload from GET_INGREDIENT_DETAILS into the
// IngredientDetail shape the view + edit pages consume.
export const mapApiToIngredientDetail = (food: any): IngredientDetail => {
  const measurements: any[] = Array.isArray(food?.measurements)
    ? food.measurements
    : [];
  const activity: any[] = Array.isArray(food?.activity) ? food.activity : [];
  const tags: string[] = Array.isArray(food?.tags)
    ? food.tags.filter((t: unknown) => typeof t === "string" && t.trim())
    : [];
  const baseQuantityRaw = String(food?.base_quantity ?? "").trim();
  const unitsRaw = String(food?.units ?? "").trim();
  const baseQuantity = `${baseQuantityRaw}${unitsRaw}`.trim();
  const fibreRaw = food?.fibre;
  const fibre =
    fibreRaw === undefined || fibreRaw === null || fibreRaw === ""
      ? undefined
      : toNumber(fibreRaw);
  return {
    id: toNumber(food?.food_id),
    name: String(food?.item_name ?? ""),
    imageUrl: resolvePictureUrl(food?.picture),
    createdBy: String(food?.created_by ?? ""),
    calories: toNumber(food?.cals),
    adjProtein: toNumber(food?.AAFAdj_protein),
    netCarbs: toNumber(food?.["net carbs"]),
    fats: toNumber(food?.total_fat),
    gl: toNumber(food?.GL),
    status: food?.hide_status ? "Archived" : "Active",
    dietType: dietTypeFromApi(food?.type),
    baseQuantity: baseQuantity || "—",
    category: String(food?.category ?? ""),
    tags,
    allergens: [],
    carbs: toNumber(food?.carbs),
    grossProtein: toNumber(food?.gross_protein),
    fiber: fibre,
    cpfRatio: String(food?.cpf ?? ""),
    micronutrients: parseMicronutrients(food?.micronutrient),
    unitConversions: measurements.map((m, idx) => ({
      unit: String(m?.measure_unit_type ?? `Unit ${idx + 1}`),
      grams: `${parseFloat(toNumber(m?.measure_gram).toFixed(2))} g`,
    })),
    activity: activity.map((entry, idx) => {
      const rawAction = String(entry?.action ?? "").toUpperCase();
      const actionMap: Record<string, IngredientActivityAction> = {
        CREATED: "created",
        ADDED: "added",
        EDITED: "edited",
        UPDATED: "edited",
        HIDDEN: "archived",
        ARCHIVED: "archived",
        RESTORED: "restored",
      };
      const action: IngredientActivityAction = actionMap[rawAction] ?? "edited";
      const { date, time } = formatActivityStamp(entry?.performed_at);
      const remark =
        typeof entry?.remarks === "string" ? entry.remarks.trim() : "";
      return {
        id: `${toNumber(food?.food_id)}-${idx}`,
        actor: String(entry?.performed_by ?? ""),
        action,
        date,
        time,
        remark: remark || undefined,
      };
    }),
  };
};
